import { useContext, useEffect, useState } from 'react';
import { Box, Checkbox, IconButton, ImageListItemBar, Modal, Skeleton } from '@mui/material';
import { ArrowBack, DriveFolderUpload, SaveAlt, Close, DeleteOutlineOutlined } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { AuthContext } from '../../providers/AuthProvider';
import Navbar from '../children/Navbar';
import { BodyDiv, ButtonsRow, CollectionImg, Header, ImageDiv, ImgCheckbox, ImageList, LinkBlack, Tabs, Text, Tab,
  SideBarRight, LeftBox, AdditionalImg, RowDiv, BigImage, CloseButton, SkeletonDiv, LinkPurple, GrayNavContainer, DividerVert} from '../../styles/styles';
import PhotosService from '../../services/photosService';
import CollectionService from '../../services/collectionService';
import TransformationService from '../../services/transformationService';
import { BLANK, CURATED, CURATED_CAP, DELETE_INITIATED, DOWNLOAD_INITIATED, DOWNLOAD_URL, EMPTY_STRING, ERROR, HOME_PATH, SUCCESS } from '../../constants';
import AdditionalIcon from '../../imgs/info.svg';
import { getTaskDistribution, trimText } from '../../services/helperService';
import Alert from '../children/Alert';
import DownloadService from '../../services/downloadService';
import DeleteService from '../../services/deleteService';

const Collection = () => {
  const { currentUser } = useContext(AuthContext);
  const [currentTab, setCurrentTab] = useState(0);
  const [photosInTab, setPhotosInTab] = useState<Photo[]>();
  const [selectMode, setSelectMode] = useState(false);
  const [imageToModal, setImageToModal] = useState<string>(EMPTY_STRING);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [collection, setCollection] = useState<PhotoCollection | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [transformation, setTransformation] = useState<Transformation | null>(null);
  const [subcollections, setSubcollections] = useState<SubCollection[]>([]);
  const [tabs, setTabs] = useState<string[]>([]);
  const [categorization, setCategorization] = useState<Categorization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<string[]>([]);
  const [targetSubCollectionId, setTargetSubCollectionId] = useState<string>(EMPTY_STRING);
  const [tabToCountMap, setTabToCountMap] = useState<Record<string, number>>();
  const [alertIsOpen, setAlertIsOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<string>(EMPTY_STRING);
  const [alertMessage, setAlertMessage] = useState<string>(EMPTY_STRING);
  const [totalPhotosInCollection, setTotalPhotosInCollection] = useState<number>();
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  const { collectionId } = useParams();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleTabClick = async (tab: string, index: number) => {
    if (tab === CURATED_CAP) {
      const curatedPhotos: Photo[] = await PhotosService.getPhotosBySubCollectionId(collectionId, EMPTY_STRING);
      setPhotosInTab(curatedPhotos);
      return;
    }
    const subCollectionPhotos: Photo[] = await PhotosService.getPhotosBySubCollectionId(collectionId, subcollections[index - 1].id);
    setPhotosInTab(subCollectionPhotos);
  };

  const handleCancelClick = () => {
    setSelectMode(false);
    setSelectedPhotoIds([]);
    setIsAllSelected(false);
  }

  const handleImgClick = async (photo: Photo) => {
    if (!selectMode) {
      setOpenImageModal(true);
      setImageToModal(photo?.url);
      return;
    }
    if (selectedPhotoIds.includes(photo?.id)) {
      setSelectedPhotoIds(selectedPhotoIds.filter(id => id !== photo?.id));
      return;
    }
    setSelectedPhotoIds([...selectedPhotoIds, photo?.id]);
  }

  const handleInfoClick = async (photoId: string) => {
    if (!transformation) {
      return;
    }
    const categorization = await TransformationService.getCategorization(transformation?.id, photoId);
    setCategorization(categorization);
    setDrawerOpen(true);
  }

  const handleSelectAll = () => {
    if (!photosInTab || photosInTab?.length === 0) {
      openAlertWithMsg(ERROR, "There are no photos to select in this subcollection.");
      return;
    } else if (selectedPhotoIds.length === photosInTab?.length) {
      setSelectedPhotoIds([]);
      setIsAllSelected(false);
      return;
    }
    setSelectedPhotoIds(photosInTab.map(photo => photo.id));
    setIsAllSelected(true);
  };

  const updateTabsCount = async (subcollectionsList: SubCollection[]) => {
    const newTabCountMap: Record<string, number> = {};
    for (const {title, id} of subcollectionsList) {
      const count = await PhotosService.getPhotosCountForSubCol(collectionId, id);
      newTabCountMap[title.toLowerCase()] = count;
    }
    const curatedCount = await PhotosService.getPhotosCountForSubCol(collectionId, EMPTY_STRING);
    newTabCountMap[CURATED] = curatedCount;
    setTabToCountMap(newTabCountMap);
  }

  const handleMoveClick = async () => {
    if (!selectedPhotoIds || selectedPhotoIds?.length === 0) {
      openAlertWithMsg(ERROR, "Please select one or more photos to move to another subcollection.");
      return;
    }
    await PhotosService.movePhotoToSubCollection(selectedPhotoIds, targetSubCollectionId);
    setPhotosInTab(photosInTab?.filter(photo => !selectedPhotoIds.includes(photo?.id)));
    await updateTabsCount(subcollections);
    handleCancelClick();
    openAlertWithMsg(SUCCESS, "Successfully moved photos!");
    return;
  }

  const handleSelectModeClick = () => {
    setSelectMode(true);
    const originSubCollectionId: string = subcollections?.find(subcollection => subcollection?.title === tabs[currentTab])?.id;
    if (originSubCollectionId) {
      setTargetSubCollectionId(EMPTY_STRING);
      return;
    }
    setTargetSubCollectionId(subcollections?.[0]?.id);
  }

  const handleDownloadClick = async () => {
    if (!selectedPhotoIds || selectedPhotoIds?.length === 0) {
      openAlertWithMsg(ERROR, "Please select one or more photos to download.");
      return;
    }
    const selectedPhotoSet = new Set(selectedPhotoIds);
    const namePathTuples: [string, string][] = photosInTab?.filter(photo => selectedPhotoSet.has(photo?.id))?.map(photo => [photo?.originalName, photo?.path]);
    const newDownloadJob: DownloadJob = {
      id: EMPTY_STRING,
      userId: currentUser?.uid,
      collectionId: collectionId,
      collectionName: collection?.title,
      status: DOWNLOAD_INITIATED,
      photoPaths: namePathTuples.reduce((acc, [name, path]) => ({ ...acc, [name]: path }), {}),
      createdAt: Timestamp.now()
    };
    const jobId: string = await DownloadService.addJob(newDownloadJob);
    if (!jobId) {
      openAlertWithMsg(ERROR, "Sorry, something went wrong. Please try again later.");
      return;
    }
    window.open(`${DOWNLOAD_URL}/${jobId}`, BLANK);
  }

  const handleDeleteClick = async () => {
    if (!selectedPhotoIds || selectedPhotoIds?.length === 0) {
      openAlertWithMsg(ERROR, "Please select one or more photos to delete.");
      return;
    }
    const { tasksDistributionMap, selectedPhotoSet } = getTaskDistribution(selectedPhotoIds, photosInTab);
    const newDeleteJob: DeleteJob = {
      id: EMPTY_STRING,
      userId: currentUser?.uid,
      status: DELETE_INITIATED,
      tasksDistribution: tasksDistributionMap,
      createdAt: Timestamp.now()
    };
    await DeleteService.addJob(newDeleteJob);
    await PhotosService.deletePhotos(selectedPhotoIds);
    const newPhotosInTab: Photo[] = photosInTab?.filter(photo => !selectedPhotoSet.has(photo?.id));
    setPhotosInTab(newPhotosInTab);
    openAlertWithMsg(SUCCESS, "Successfully deleted photos!");
    await updateTabsCount(subcollections);
    const newColTotal: number = totalPhotosInCollection - selectedPhotoIds.length;
    setTotalPhotosInCollection(newColTotal);
    await CollectionService.updateCollection({
      id: collectionId,
      totalPhotos: newColTotal,
      thumbnailUrl: newColTotal === 0 ? EMPTY_STRING : newPhotosInTab?.[0]?.url,
      updatedAt: Timestamp.now()
    });
    handleCancelClick();
  }

  const getCollectionAndTrans = async () => {
    const collection: PhotoCollection = await CollectionService.getCollectionById(collectionId);
    if (!collection) {
      return;
    }
    setCollection(collection);
    const count: number = await PhotosService.getPhotosCountForCol(collectionId)
    setTotalPhotosInCollection(count);
    const transformation = await TransformationService.getLatestTransformation(collectionId);
    if (transformation) {
      setTransformation(transformation);
    }
  }

  const getTitleForPhoto = (photo: Photo) => {
    return (
      <RowDiv style={{ padding: "2px 5px" }}>
        <Text style={{ cursor: "pointer" }} onClick={() => handleImgClick(photo)}>
          {trimText(photo?.originalName, 15)}
        </Text>
        <IconButton onClick={() => handleInfoClick(photo?.id)}>
          <AdditionalImg src={AdditionalIcon} />
        </IconButton>
      </RowDiv>
    )
  }

  const openAlertWithMsg = (type: string, msg: string) => {
    setAlertIsOpen(true);
    setAlertType(type);
    setAlertMessage(msg);
  }

  const setupSubcollectionsPhotos = async () => {
    const fetchedSubcollections: SubCollection[] = await CollectionService.getSubcollections(collectionId);
    if (!fetchedSubcollections) {
      openAlertWithMsg(ERROR, "Sorry, something went wrong. Please try again later.");
      return;
    }
    setSubcollections(fetchedSubcollections);
    await updateTabsCount(fetchedSubcollections);
    setTabs([CURATED_CAP].concat(fetchedSubcollections?.map(subcollection => subcollection?.title)));
    const photos: Photo[] = await PhotosService.getPhotosBySubCollectionId(collectionId, EMPTY_STRING);
    setPhotosInTab(photos);
    setIsLoading(false);
  }

  useEffect(() => {
    if (collectionId) {
      getCollectionAndTrans();
      setupSubcollectionsPhotos();
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      window.location.assign(HOME_PATH);
    }
  }, [currentUser]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const drawer = document.querySelector('.MuiDrawer-paper');
      if (drawerOpen && drawer && !drawer.contains(event.target as Node)) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [drawerOpen]);

  return (
    <>
      <Navbar />
      <BodyDiv>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <RowDiv>
            <div>
              <LinkBlack onClick={() => window.location.assign(HOME_PATH)} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "24px" }}>
                <ArrowBack style={{ fontSize: "16px" }} />My collections
              </LinkBlack>
              <Header style={{ marginTop: "32px" }}>{collection?.title}</Header>
              {totalPhotosInCollection && <Text style={{ marginTop: "12px", marginBottom: "12px" }}>{totalPhotosInCollection} photos</Text>}
            </div>
            {selectMode ?
              <GrayNavContainer style={{ marginBottom: "0px", gap: "100px" }}>
                <RowDiv style={{ gap: "12px" }}>
                  <IconButton onClick={handleCancelClick}><Close style={{ color: "#1E1E1E" }} /></IconButton>
                  <Text style={{ whiteSpace: 'nowrap', color: "#1E1E1E" }}>{selectedPhotoIds?.length} Selected</Text>
                </RowDiv>
                <RowDiv style={{ gap: "15px" }}>
                  <RowDiv>
                    <Checkbox checked={isAllSelected} onChange={handleSelectAll} style={{ color: "#A20B91" }} />
                    <Text onClick={handleSelectAll} style={{ whiteSpace: 'nowrap', color: "#1E1E1E", cursor: "pointer" }}>Select all</Text>
                  </RowDiv>
                  <DividerVert />
                  <ButtonsRow>
                    <IconButton onClick={handleDownloadClick}><SaveAlt style={{ color: "#A20B91" }} /></IconButton>
                    <IconButton onClick={handleMoveClick}><DriveFolderUpload style={{ color: "#A20B91" }} /></IconButton>
                    <IconButton onClick={handleDeleteClick}><DeleteOutlineOutlined style={{ color: "#A20B91" }} /></IconButton>
                  </ButtonsRow>
                </RowDiv>
              </GrayNavContainer>
              :
              <LinkPurple onClick={handleSelectModeClick} style={{ padding: "10px 24px" }}>Select</LinkPurple>
            }
          </RowDiv>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="Photo categories">
            {tabs?.map((tab, index) => (
              <Tab key={index} label={`${tab} (${tabToCountMap?.[tab.toLowerCase()]})`} id={index.toString()} onClick={() => handleTabClick(tab, index)} />
            ))}
          </Tabs>
        </Box>
        <SkeletonDiv>
          {isLoading &&
            [1, 2, 3, 4].map((index) => (
              <Skeleton variant="rounded" width={210} height={200} key={index} />
            ))
          }
        </SkeletonDiv>
        <ImageList variant="masonry" cols={6} gap={18} style={{ marginTop: "32px" }}>
          {!isLoading && photosInTab?.length > 0 &&
            photosInTab?.map(photo => (
              <ImageDiv key={photo?.id} checked={selectedPhotoIds?.includes(photo?.id)} selecting={selectMode}>
                <CollectionImg srcSet={photo?.url} src={photo?.url} loading="lazy" onClick={() => handleImgClick(photo)} />
                <ImageListItemBar title={getTitleForPhoto(photo)} position="below"
                  sx={{
                    '& .MuiImageListItemBar-titleWrap': {
                      padding: 0
                    }
                  }}
                />
                <ImgCheckbox className="checkbox" checked={selectedPhotoIds?.includes(photo?.id)} />
              </ImageDiv>
            ))
          }
        </ImageList>
      </BodyDiv>
      <Modal open={openImageModal} onClose={() => setOpenImageModal(false)} disableAutoFocus={true}>
        <div>
          <CloseButton onClick={() => setOpenImageModal(false)}><Close /></CloseButton>
          <BigImage src={imageToModal} />
        </div>
      </Modal>
      <SideBarRight hideBackdrop anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <LeftBox>
          <Header style={{ marginTop: "0px" }}>Prompt</Header>
          <Header>Model explanation</Header>
          <Text>{categorization?.modelExplanation}</Text>
          <br />
          {categorization?.ratings && Object.entries(categorization.ratings).map(([key, value], index) => (
            <div key={index}>
              <Text>{key}: {value}</Text>
              <br />
            </div>
          ))}
        </LeftBox>
      </SideBarRight>
      <Alert alertType={alertType} alertIsOpen={alertIsOpen} alertMessage={alertMessage} setAlertIsOpen={setAlertIsOpen} />
    </>
  );
};

export default Collection;
